import {
  cargoSubtype,
  CompanyEntity,
  ContainerEntity,
  firstOrUndefined,
  FreightTypeEntity,
  getObjectId,
  isEmptyOrNullOrUndefined,
  PersonEntity,
  Quantity,
  ServiceTypeEntity,
  SiteEntity,
  sitesSelector,
  toTransportCategory,
  TrailerEntity,
  transportCategories,
  transportCategory,
  TransportCategory,
  transportType,
  UnitOfMeasureEntity,
  useGetAllFreightTypesQuery,
  useGetAllSitesQuery,
  useGetPreferredFreightType,
  useGetPreferredSite,
  VehicleEntity,
  WellEntity
} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Modal,
  ModalProps,
  NumberInput,
  SegmentedControl,
  Switch,
  Textarea,
  TextInput,
  Title
} from "@mantine/core";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {ServiceTypeSelect} from "../../select/entity/ServiceTypeSelect/ServiceTypeSelect";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";
import {WellSelect} from "../../select/entity/WellSelect/WellSelect";
import {getTransportCategoryColorRefByType} from "../../../util/transport-category-ui-util";
import {DriverSelect} from "../../select/entity/PersonSelect/DriverSelect";
import {VehicleSelect} from "../../select/entity/VehicleSelect/VehicleSelect";
import {TrailerSelect} from "../../select/entity/TrailerSelect/TrailerSelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {useDispatchForm} from "./useDispatchForm";
import {IconTrash} from "@tabler/icons-react";
import {SplitActions} from "./SplitActions";
import {notifications} from "@mantine/notifications";
import {TruckCompanySelect} from "../../select/entity/CompanySelect/TruckCompanySelect";
import {PreferenceAwareContainerSelect} from "../../select/entity/ContainerSelect/PreferenceAwareContainerSelect";

export interface DispatchFormProps {
  options?: DispatchFormOptions;
  defaultValues?: Partial<DispatchFormValues>;
  onSubmit?: (values: DispatchFormValues) => void;
  submitting?: boolean;
}

export interface DispatchFormOptions {
  regionId?: string
  category?: TransportCategory
  categoryDisabled?: boolean
  companySelectionDisabled?: boolean
  personSelectionDisabled?: boolean;
  pickupSiteSelectionDisabled?: boolean;
  pickupWellSelectionDisabled?: boolean;
  pickupContainerSelectionDisabled?: boolean;
  deliverySiteSelectionDisabled?: boolean;
  deliveryWellSelectionDisabled?: boolean;
  deliveryContainerSelectionDisabled?: boolean;
  splitsDisabled?: boolean
  loadDetailsDisabled?: boolean,
  copyDispatchRequestCommentsVisible?: boolean
  crudeOilFreightTypeDefaultEnabled?: boolean
}

export interface DispatchFormLocationValues {
  siteCompany?: CompanyEntity | string
  site?: SiteEntity | string;
  well?: WellEntity | string;
  container?: ContainerEntity | string;
}

export interface DispatchFormSplitValues {
  type: "PICKUP" | "DELIVERY"
  quantity?: Partial<Quantity>;
  freightType?: FreightTypeEntity | string;
  serviceType?: ServiceTypeEntity | string;
  location?: DispatchFormLocationValues
  comments?: string;
}

export interface DispatchFormValues {
  date: Date;
  company?: CompanyEntity | string;
  person?: PersonEntity | string;
  vehicle?: VehicleEntity | string;
  trailer?: TrailerEntity | string;
  quantity: Quantity;
  freightType?: FreightTypeEntity | string;
  serviceType?: ServiceTypeEntity | string;
  pickup?: DispatchFormLocationValues
  delivery?: DispatchFormLocationValues
  splits?: DispatchFormSplitValues[]
  workOrderNumber?: string;
  comments?: string;
  category?: TransportCategory
  copyDispatchRequestComments?: boolean
}

export interface DispatchFormDefaultValues extends Partial<Omit<DispatchFormValues, 'quantity'>> {
  quantity?: Partial<Quantity>
}

export interface DispatchFormLocationRawValues {
  siteCompany?: string
  site?: string;
  well?: string;
  container?: string;
}

export interface DispatchFormSplitRawValues {
  type: "PICKUP" | "DELIVERY"
  quantity?: number
  quantityUom?: string;
  freightType?: string;
  serviceType?: string;
  location?: DispatchFormLocationRawValues
  comments?: string;
}

export interface DispatchFormRawValues {
  date: Date;
  company?: string;
  person?: string;
  vehicle?: string
  trailer?: string
  quantity?: number
  quantityUom?: string;
  freightType?: string;
  serviceType?: string;
  pickup?: DispatchFormLocationRawValues
  delivery?: DispatchFormLocationRawValues
  splits?: DispatchFormSplitRawValues[]
  workOrderNumber?: string;
  dispatchComments?: string;
  comments?: string;
  category?: string
  copyDispatchRequestComments?: boolean
}

export type DispatchFormType = UseFormReturnType<DispatchFormRawValues, (values: DispatchFormRawValues) => DispatchFormRawValues>

export const DispatchForm = (props: DispatchFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const [category, setCategory] = useState<TransportCategory>(defaultValues?.category ?? options?.category ?? transportCategory.water)

  const form = useDispatchForm({
    defaultValues: defaultValues,
  });

  const formRef = useRef(form);
  formRef.current = form;

  const deliverySiteSelectionDisabledRef = useRef(options?.deliverySiteSelectionDisabled);
  deliverySiteSelectionDisabledRef.current = options?.deliverySiteSelectionDisabled;

  const selectSites = useMemo(() => {
    return sitesSelector()
  }, []);

  const {sites} = useGetAllSitesQuery(options?.regionId, {
    selectFromResult: (result) => ({
      sites: selectSites(result, {}),
    })
  });

  const sitesById = useMemo(() => {
    return new Map(sites.map(s => [s.id, s]))
  }, [sites]);

  const {siteId: preferredDeliverySiteId} = useGetPreferredSite({
    serviceWork: !isEmptyOrNullOrUndefined(form.values.serviceType),
    siteId: form.values.pickup?.site,
    regionId: options?.regionId,
    type: transportType.delivery,
    category: category
  });

  const {freightTypeId: preferredFreightTypeId} = useGetPreferredFreightType({
    serviceTypeId: getObjectId(form.values.serviceType),
    regionId: options?.regionId,
  });

  const {data: freightTypes = []} = useGetAllFreightTypesQuery(options?.regionId);
  const crudeOilFreightType = useMemo(() => firstOrUndefined(
    freightTypes.filter((freightType) =>
      freightType.cargoSubType === cargoSubtype.oil.code &&
      !freightType.doNotUse &&
      freightType.type.toLowerCase() === "crude oil"
    )
  ), [freightTypes]);

  const pickupSiteId = form.values?.pickup?.site;

  useEffect(() => {
    const f = formRef.current;
    const disabled = !!deliverySiteSelectionDisabledRef.current;

    if (disabled || !preferredDeliverySiteId) {
      return;
    }

    const currentDeliverySiteId =
      f.getTransformedValues()?.delivery?.site ??
      f.values?.delivery?.site

    if (currentDeliverySiteId !== preferredDeliverySiteId) {
      const site = sitesById.get(preferredDeliverySiteId);
      f.setFieldValue("delivery.siteCompany", site?.companyId);
      f.setFieldValue("delivery.site", preferredDeliverySiteId);
    }
  }, [preferredDeliverySiteId, pickupSiteId, sitesById]);

  useEffect(() => {
    if (preferredFreightTypeId && !form.values.freightType) {
      form.setFieldValue("freightType", preferredFreightTypeId);
    }
  }, [form, preferredFreightTypeId]);

  useEffect(() => {
    if (
      options?.crudeOilFreightTypeDefaultEnabled &&
      category === transportCategory.oil &&
      crudeOilFreightType &&
      !form.values.freightType
    ) {
      form.setFieldValue("freightType", crudeOilFreightType.id);
    }
  }, [
    category,
    crudeOilFreightType,
    form,
    form.values.freightType,
    options?.crudeOilFreightTypeDefaultEnabled
  ]);

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      const pickup: DispatchFormLocationValues = {
        siteCompany: values.pickup?.siteCompany,
        site: values.pickup?.site,
        well: values.pickup?.well,
        container: values.pickup?.container,
      }

      const delivery: DispatchFormLocationValues = {
        siteCompany: values.delivery?.siteCompany,
        site: values.delivery?.site,
        well: values.delivery?.well,
        container: values.delivery?.container,
      }

      const splits: DispatchFormSplitValues[] | undefined = values.splits?.map((splitValue) => {

        let comments: string | undefined = undefined
        if (splitValue.comments) {
          comments = splitValue.comments
        }
        if (values.comments) {
          if (!comments) {
            comments = ""
          }

          comments += `\n\n\n ${values.comments}`
        }

        return {
          type: splitValue.type,
          quantity: {
            number: splitValue.quantity,
            units: splitValue.quantityUom,
          },
          freightType: splitValue.freightType,
          serviceType: splitValue.serviceType,
          location: {
            siteCompany: splitValue.location?.siteCompany,
            site: splitValue.location?.site,
            well: splitValue.location?.well,
            container: splitValue.location?.container,
          },
          comments: comments,
        }
      })

      let comments: string | undefined = undefined
      if (values.dispatchComments) {
        comments = values.dispatchComments
      }
      if (values.comments) {
        if (!comments) {
          comments = ""
        }

        comments += `\n\n\n ${values.comments}`
      }

      const submitValues: DispatchFormValues = {
        date: values.date,
        company: values.company,
        person: values.person,
        vehicle: values.vehicle,
        trailer: values.trailer,
        quantity: {
          number: values.quantity!,
          units: values.quantityUom!
        },
        freightType: values.freightType,
        serviceType: values.serviceType,
        pickup: pickup,
        delivery: delivery,
        splits: splits,
        workOrderNumber: values.workOrderNumber,
        comments: comments,
        copyDispatchRequestComments: values.copyDispatchRequestComments
      }

      let error = false
      if (!submitValues.pickup?.site && !submitValues.delivery?.site) {
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: t<string>("common.entity.dispatch.form.error.location", "A pickup or delivery location is required."),
          autoClose: true
        });
        error = true
      }

      if (splits) {
        let splitError = false
        splits.forEach((split, i) => {
          if (!split.location?.site) {
            form.setFieldError(`splits.${i}.location.site`, t("common.validation.required", "Required"))
            splitError = true
          }
        })

        if (splitError) {

          if (!error) {
            notifications.show({
              color: "red",
              title: t("common.error", "Error"),
              message: t<string>("common.entity.dispatch.form.error.split.location", "A location is required on split jobs."),
              autoClose: true
            });
          }

          error = true
        }
      }

      if (!error) {
        onSubmit(submitValues);
      }
    }
  }, [form, onSubmit, t]);

  const handleCompanyChange = useCallback((
    company: CompanyEntity | undefined,
  ): void => {
    form.setFieldValue("person", undefined);
    form.setFieldValue("vehicle", undefined);
    form.setFieldValue("trailer", undefined);
  }, [form]);

  const handleSiteCompanyChange = useCallback((
    company: CompanyEntity | undefined,
    type: "PICKUP" | "DELIVERY"
  ): void => {
    if (type === "PICKUP") {
      form.setFieldValue("pickup.site", undefined);
      form.setFieldValue("pickup.container", undefined);
      form.setFieldValue("pickup.well", undefined);
    } else if (type === "DELIVERY") {
      form.setFieldValue("delivery.site", undefined);
      form.setFieldValue("delivery.container", undefined);
      form.setFieldValue("delivery.well", undefined);
    }
  }, [form]);

  const handleSiteChange = useCallback((
    site: SiteEntity | undefined,
    type: "PICKUP" | "DELIVERY"
  ): void => {
    if (site) {
      const selectedPickupSiteCompanyId = form.getTransformedValues().pickup?.siteCompany;
      const selectedDeliverySiteCompanyId = form.getTransformedValues().delivery?.siteCompany;

      if (site.companyId) {
        if (type === "PICKUP" && selectedPickupSiteCompanyId !== site.companyId) {
          form.setFieldValue("pickup.siteCompany", site.companyId);
        } else if (type === "DELIVERY" && selectedDeliverySiteCompanyId !== site.companyId) {
          form.setFieldValue("delivery.siteCompany", site.companyId);
        }
      }
    }

    if (type === "PICKUP") {
      form.setFieldValue("pickup.container", undefined);
      form.setFieldValue("pickup.well", undefined);
    } else if (type === "DELIVERY") {
      form.setFieldValue("delivery.container", undefined);
      form.setFieldValue("delivery.well", undefined);
    }
  }, [form]);

  /**
   * Handles when a pickup/delivery well changes. Resets the pickup/container to undefined.
   *
   * @param wellId the primary key of the selected well
   * @param type transport type
   */
  const handleWellChange = useCallback((
    well: WellEntity | undefined,
    type: "PICKUP" | "DELIVERY"
  ): void => {
    if (well) {
      const selectedPickupSiteCompanyId = form.getTransformedValues().pickup?.siteCompany;
      const selectedDeliverySiteCompanyId = form.getTransformedValues().delivery?.siteCompany;

      if (well.siteCompanyId) {
        if (type === "PICKUP" && selectedPickupSiteCompanyId !== well.siteCompanyId) {
          form.setFieldValue("pickup.siteCompany", well.siteCompanyId);
        } else if (type === "DELIVERY" && selectedDeliverySiteCompanyId !== well.siteCompanyId) {
          form.setFieldValue("delivery.siteCompany", well.siteCompanyId);
        }
      }

      const selectedPickupSiteId = form.getTransformedValues().pickup?.site;
      const selectedDeliverySiteId = form.getTransformedValues().delivery?.site;

      if (well.siteId) {
        if (type === "PICKUP" && selectedPickupSiteId !== well.siteId) {
          form.setFieldValue("pickup.site", well.siteId);
        } else if (type === "DELIVERY" && selectedDeliverySiteId !== well.siteId) {
          form.setFieldValue("delivery.site", well.siteId);
        }
      }
    }
  }, [form]);

  /**
   * Handles when a pickup/delivery container changes. Sets the pickup/delivery site to selected container's site.
   *
   * @param containerId the primary key of the selected container
   * @param type transport type
   */
  const handleContainerChange = useCallback((container: ContainerEntity | undefined, type: "PICKUP" | "DELIVERY"): void => {
    if (container) {
      const selectedPickupSiteCompanyId = form.getTransformedValues().pickup?.siteCompany;
      const selectedDeliverySiteCompanyId = form.getTransformedValues().delivery?.siteCompany;

      if (container.siteCompanyId) {
        if (type === "PICKUP" && selectedPickupSiteCompanyId !== container.siteCompanyId) {
          form.setFieldValue("pickup.siteCompany", container.siteCompanyId);
        } else if (type === "DELIVERY" && selectedDeliverySiteCompanyId !== container.siteCompanyId) {
          form.setFieldValue("delivery.siteCompany", container.siteCompanyId);
        }
      }

      const selectedPickupSiteId = form.getTransformedValues().pickup?.site;
      const selectedDeliverySiteId = form.getTransformedValues().delivery?.site;

      if (container.siteId) {
        if (type === "PICKUP" && selectedPickupSiteId !== container.siteId) {
          form.setFieldValue("pickup.site", container.siteId);
        } else if (type === "DELIVERY" && selectedDeliverySiteId !== container.siteId) {
          form.setFieldValue("delivery.site", container.siteId);
        }
      }
    }
  }, [form]);

  const handleSplitSiteCompanyChange = useCallback((company: CompanyEntity | undefined, item: DispatchFormSplitRawValues, index: number): void => {
    form.setFieldValue(`splits.${index}.location.site`, undefined);
    form.setFieldValue(`splits.${index}.location.container`, undefined);
    form.setFieldValue(`splits.${index}.location.well`, undefined);
  }, [form]);

  const handleSplitSiteChange = useCallback((site: SiteEntity | undefined, item: DispatchFormSplitRawValues, index: number): void => {
    if (site) {
      const selectedPickupSiteCompanyId = item.location?.siteCompany;

      if (site.companyId) {
        if (selectedPickupSiteCompanyId !== site.companyId) {
          form.setFieldValue(`splits.${index}.location.siteCompany`, site.companyId);
        }
      }
    }

    form.setFieldValue(`splits.${index}.location.container`, undefined);
    form.setFieldValue(`splits.${index}.location.well`, undefined);
  }, [form]);

  const handleSplitWellChange = useCallback((well: WellEntity | undefined, item: DispatchFormSplitRawValues, index: number): void => {
    if (well) {
      const selectedSiteCompanyId = item.location?.siteCompany;

      if (well.siteCompanyId) {
        if (selectedSiteCompanyId !== well.siteCompanyId) {
          form.setFieldValue(`splits.${index}.location.siteCompany`, well.siteCompanyId);
        }
      }

      const selectedSiteId = item.location?.site;

      if (well.siteId) {
        if (selectedSiteId !== well.siteId) {
          form.setFieldValue(`splits.${index}.location.site`, well.siteId);
        }
      }
    }
  }, [form]);

  const handleSplitContainerChange = useCallback((container: ContainerEntity | undefined, item: DispatchFormSplitRawValues, index: number): void => {
    if (container) {
      const selectedSiteCompanyId = item.location?.siteCompany

      if (container.siteCompanyId) {
        if (selectedSiteCompanyId !== container.siteCompanyId) {
          form.setFieldValue(`splits.${index}.location.siteCompany`, container.siteCompanyId);
        }
      }

      const selectedSiteId = item.location?.site;

      if (container.siteId) {
        if (selectedSiteId !== container.siteId) {
          form.setFieldValue(`splits.${index}.location.site`, container.siteId);
        }
      }
    }
  }, [form]);

  const onCategoryChange = useCallback((value: string) => {
    const newCategory = toTransportCategory(value)

    setCategory(newCategory)

    if (newCategory === transportCategory.oil) {
      form.setFieldValue("serviceType", undefined)
    }
  }, [form])

  const onAddSplitActionClick = useCallback((type: "PICKUP" | "DELIVERY") => {
    form.setFieldValue('splits', form.values.splits ?? [])

    const location = type === "PICKUP" ? form.values.pickup : form.values.delivery

    const quantity = form.values.quantityUom === "loads" ? 1.0 : undefined

    form.insertListItem('splits', {
      type: type,
      quantity: quantity,
      quantityUom: form.values.quantityUom,
      location: {
        siteCompany: location?.siteCompany,
        site: location?.site,
      }
    })
  }, [form])

  const onRemoveSplitActionClick = useCallback((index: number) => {
    form.removeListItem('splits', index)
  }, [form])

  const splits = form.values.splits
  const splitsType = useMemo(() => {
    return firstOrUndefined(splits)?.type
  }, [splits])

  const pickupSplitDisabled = useMemo(() => {
    const currType = splitsType
    return options?.splitsDisabled || (currType && currType !== "PICKUP")
  }, [options?.splitsDisabled, splitsType])

  const deliverySplitDisabled = useMemo(() => {
    const currType = splitsType
    return options?.splitsDisabled || (currType && currType !== "DELIVERY")
  }, [options?.splitsDisabled, splitsType])

  const onQuantityUomChange = useCallback((uoms: UnitOfMeasureEntity[]) => {
    const uom = firstOrUndefined(uoms)
    form.getTransformedValues().splits?.forEach((_item, index) => {
      form.setFieldValue(`splits.${index}.quantityUom`, uom?.name)
    })
  }, [form])

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("common.entity.dispatch.form.field.date", "Scheduled Date")}
            placeholder={t<string>("common.entity.dispatch.form.field.date.placeholder", "Scheduled Date")}
            styles={{wrapper: {width: "50%"}}}
            withAsterisk
            required
            {...form.getInputProps("date")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <TruckCompanySelect
            disabled={options?.companySelectionDisabled}
            label={t("common.entity.dispatch.form.field.company", "Company")}
            placeholder={t<string>("common.entity.dispatch.form.field.company.placeholder", "Company")}
            selector={{
              regionId: options?.regionId
            }}
            onEntityChange={(e) => handleCompanyChange(firstOrUndefined(e))}
            clearable
            {...form.getInputProps("company")} />
          <DriverSelect
            disabled={options?.personSelectionDisabled}
            label={t("common.entity.dispatch.form.field.person", "Driver")}
            placeholder={t<string>("common.entity.dispatch.form.field.person.placeholder", "Driver")}
            selector={{
              regionId: options?.regionId,
              companyId: form.getTransformedValues().company,
            }}
            clearable
            {...form.getInputProps("person")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <VehicleSelect
            label={t("common.entity.dispatch.form.field.vehicle", "Truck")}
            placeholder={t<string>("common.entity.dispatch.form.field.vehicle.placeholder", "Truck")}
            selector={{
              regionId: options?.regionId,
              companyId: form.getTransformedValues().company,
            }}
            clearable
            {...form.getInputProps("vehicle")} />
          <TrailerSelect
            label={t("common.entity.dispatch.form.field.trailer", 'Trailer')}
            placeholder={t<string>("common.entity.dispatch.form.field.trailer.placeholder", "Trailer")}
            selector={{
              regionId: options?.regionId,
              companyId: form.getTransformedValues().company,
            }}
            clearable
            {...form.getInputProps("trailer")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Group grow>
        <NumberInput
          disabled={options?.loadDetailsDisabled}
          label={t("common.entity.dispatch.form.field.quantity", "Quantity")}
          placeholder={t<string>("common.entity.dispatch.form.field.quantity.placeholder", "Quantity")}
          precision={5}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          withAsterisk
          required
          {...form.getInputProps("quantity")} />
        <UnitOfMeasureSelect
          disabled={options?.loadDetailsDisabled}
          label={t("common.entity.dispatch.form.field.uom", "Units")}
          placeholder={t<string>("common.entity.dispatch.form.field.uom.placeholder", "Units")}
          onEntityChange={onQuantityUomChange}
          clearable
          withAsterisk
          required
          {...form.getInputProps("quantityUom")} />
      </Group>
      <Divider my="sm" variant="dotted"/>
      <SegmentedControl
        my={"md"}
        value={category.code}
        onChange={onCategoryChange}
        color={getTransportCategoryColorRefByType(category)}
        data={transportCategories.map((c) => {
          return {
            label: t<string>(c.displayValue.id, c.displayValue.defaultMessage),
            value: c.code,
            disabled: options?.categoryDisabled
          }
        })}
      />
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <FreightTypeSelect
            disabled={options?.loadDetailsDisabled}
            label={t("common.entity.dispatch.form.field.freighttype", "Freight Type")}
            placeholder={t<string>("common.entity.dispatch.form.field.freighttype.placeholder", "Freight Type")}
            selector={{
              regionId: options?.regionId,
              cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined
            }}
            defaultToFirst={category === transportCategory.oil}
            clearable
            withAsterisk
            required
            {...form.getInputProps("freightType")} />
        </Grid.Col>
        <Grid.Col span={4}>
          {category === transportCategory.water &&
            <ServiceTypeSelect
              disabled={options?.loadDetailsDisabled}
              label={t("common.entity.dispatch.form.field.servicetype", "Service Type")}
              placeholder={t<string>("common.entity.dispatch.form.field.servicetype.placeholder", "Service Type")}
              selector={{
                regionId: options?.regionId,
              }}
              clearable
              {...form.getInputProps("serviceType")} />
          }
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <CompanySelect
            disabled={options?.pickupSiteSelectionDisabled}
            label={t("common.entity.dispatch.form.field.pickupsitecompany", "Pickup Site Company")}
            placeholder={t<string>("common.entity.dispatch.form.field.pickupsitecompany.placeholder", "Pickup Site Company")}
            selector={{
              regionId: options?.regionId,
            }}
            onEntityChange={(e) => handleSiteCompanyChange(firstOrUndefined(e), "PICKUP")}
            clearable
            {...form.getInputProps("pickup.siteCompany")} />
          <SiteSelect
            disabled={options?.pickupSiteSelectionDisabled}
            label={t("common.entity.dispatch.form.field.pickupsite", "Pickup Site")}
            placeholder={t<string>("common.entity.dispatch.form.field.pickupsite.placeholder", "Pickup Site")}
            selector={{
              regionId: options?.regionId,
              company: form.getTransformedValues().pickup?.siteCompany,
            }}
            onEntityChange={(e) => handleSiteChange(firstOrUndefined(e), "PICKUP")}
            clearable
            {...form.getInputProps("pickup.site")} />
          <WellSelect
            disabled={options?.pickupWellSelectionDisabled}
            label={t("common.entity.dispatch.form.field.pickupwell", "Pickup Well")}
            placeholder={t<string>("common.entity.dispatch.form.field.pickupwell.placeholder", "Pickup Well")}
            selector={{
              regionId: options?.regionId,
              siteId: form.getTransformedValues().pickup?.site
            }}
            onEntityChange={(e) => handleWellChange(firstOrUndefined(e), "PICKUP")}
            clearable
            {...form.getInputProps("pickup.well")} />
          <PreferenceAwareContainerSelect
            disabled={options?.pickupContainerSelectionDisabled}
            label={t("common.entity.dispatch.form.field.pickupcontainer", "Pickup Container")}
            placeholder={t<string>("common.entity.dispatch.form.field.pickupcontainer.placeholder", "Pickup Container")}
            useAbbreviation={typeof form.getTransformedValues().pickup?.site !== "undefined"}
            showCargoSubtype
            regionId={options?.regionId}
            category={category}
            companyId={form.getTransformedValues().pickup?.siteCompany}
            siteId={form.getTransformedValues().pickup?.site}
            containerId={form.getTransformedValues().pickup?.container}
            freightTypeId={form.getTransformedValues().freightType}
            serviceTypeId={form.getTransformedValues().serviceType}
            onEntityChange={(e) => handleContainerChange(firstOrUndefined(e), "PICKUP")}
            clearable
            {...form.getInputProps("pickup.container")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <CompanySelect
            disabled={options?.deliverySiteSelectionDisabled}
            label={t("common.entity.dispatch.form.field.deliverysitecompany", "Delivery Site Company")}
            placeholder={t<string>("common.entity.dispatch.form.field.deliverysitecompany.placeholder", "Delivery Site Company")}
            selector={{
              regionId: options?.regionId,
            }}
            onEntityChange={(e) => handleSiteCompanyChange(firstOrUndefined(e), "DELIVERY")}
            clearable
            {...form.getInputProps("delivery.siteCompany")} />
          <SiteSelect
            disabled={options?.deliverySiteSelectionDisabled}
            label={t("common.entity.dispatch.form.field.deliverysite", "Delivery Site")}
            placeholder={t<string>("common.entity.dispatch.form.field.deliverysite.placeholder", "Delivery Site")}
            selector={{
              regionId: options?.regionId,
              company: form.getTransformedValues().delivery?.siteCompany,
            }}
            onEntityChange={(e) => handleSiteChange(firstOrUndefined(e), "DELIVERY")}
            clearable
            {...form.getInputProps("delivery.site")} />
          <WellSelect
            disabled={options?.deliveryWellSelectionDisabled}
            label={t("common.entity.dispatch.form.field.deliverywell", "Delivery Well")}
            placeholder={t<string>("common.entity.dispatch.form.field.deliverywell.placeholder", "Delivery Well")}
            selector={{
              regionId: options?.regionId,
              siteId: form.getTransformedValues().delivery?.site
            }}
            onEntityChange={(e) => handleWellChange(firstOrUndefined(e), "DELIVERY")}
            clearable
            {...form.getInputProps("delivery.well")} />
          <PreferenceAwareContainerSelect
            disabled={options?.deliveryContainerSelectionDisabled}
            label={t("common.entity.dispatch.form.field.deliverycontainer", "Delivery Container")}
            placeholder={t<string>("common.entity.dispatch.form.field.deliverycontainer.placeholder", "Delivery Container")}
            useAbbreviation={typeof form.getTransformedValues().delivery?.site !== "undefined"}
            showCargoSubtype
            regionId={options?.regionId}
            category={category}
            companyId={form.getTransformedValues().delivery?.siteCompany}
            siteId={form.getTransformedValues().delivery?.site}
            containerId={form.getTransformedValues().delivery?.container}
            freightTypeId={form.getTransformedValues().freightType}
            serviceTypeId={form.getTransformedValues().serviceType}
            onEntityChange={(e) => handleContainerChange(firstOrUndefined(e), "DELIVERY")}
            clearable
            {...form.getInputProps("delivery.container")} />
        </Grid.Col>
      </Grid>
      {firstOrUndefined(form.values.splits) &&
        <Textarea
          label={t("common.entity.dispatch.form.field.comments", "Comments")}
          placeholder={t<string>("common.entity.dispatch.form.field.comments.placeholder", "Comment")}
          {...form.getInputProps("dispatchComments")} />
      }
      {form.values.splits?.map((item, index) => (
        <>
          <Divider my="sm" variant="dotted"/>
          <Box p="sm" bg={index % 2 === 0 ? "gray.1" : undefined}>
            <Group my="md">
              <Title order={4}>
                {(item.type === "PICKUP" ? "Pickup" : "Delivery") + ` Split ${index + 1}`}
              </Title>
              <ActionIcon onClick={(e) => onRemoveSplitActionClick(index)}>
                <IconTrash size="1.2rem"/>
              </ActionIcon>
            </Group>
            <Grid grow gutter="xs">
              <Grid.Col span={4}>
                <CompanySelect
                  label={t("common.entity.dispatch.form.field.sitecompany", "Site Company")}
                  placeholder={t<string>("common.entity.dispatch.form.field.sitecompany.placeholder", "Site Company")}
                  selector={{
                    regionId: options?.regionId,
                  }}
                  onEntityChange={(e) => handleSplitSiteCompanyChange(firstOrUndefined(e), item, index)}
                  clearable
                  {...form.getInputProps(`splits.${index}.location.siteCompany`)} />
                <SiteSelect
                  label={t("common.entity.dispatch.form.field.site", "Site")}
                  placeholder={t<string>("common.entity.dispatch.form.field.site.placeholder", "Site")}
                  selector={{
                    regionId: options?.regionId,
                    company: item.location?.siteCompany,
                  }}
                  onEntityChange={(e) => handleSplitSiteChange(firstOrUndefined(e), item, index)}
                  clearable
                  {...form.getInputProps(`splits.${index}.location.site`)} />
                <WellSelect
                  label={t("common.entity.dispatch.form.field.well", "Well")}
                  placeholder={t<string>("common.entity.dispatch.form.field.well.placeholder", "Well")}
                  selector={{
                    regionId: options?.regionId,
                    siteId: item.location?.site
                  }}
                  onEntityChange={(e) => handleSplitWellChange(firstOrUndefined(e), item, index)}
                  clearable
                  {...form.getInputProps(`splits.${index}.location.well`)} />
                <PreferenceAwareContainerSelect
                  label={t("common.entity.dispatch.form.field.container", "Container")}
                  placeholder={t<string>("common.entity.dispatch.form.field.container.placeholder", "Container")}
                  useAbbreviation={typeof item.location?.site !== "undefined"}
                  showCargoSubtype
                  regionId={options?.regionId}
                  category={category}
                  companyId={item.location?.siteCompany}
                  siteId={item.location?.site}
                  containerId={item.location?.container}
                  freightTypeId={item.freightType ?? form.values.freightType}
                  serviceTypeId={item.serviceType ?? form.values.serviceType}
                  onEntityChange={(e) => handleSplitContainerChange(firstOrUndefined(e), item, index)}
                  clearable
                  {...form.getInputProps(`splits.${index}.location.container`)} />
              </Grid.Col>
              <Grid.Col span={4}>
                <Group grow>
                  <NumberInput
                    label={t("common.entity.dispatch.form.field.quantity", "Quantity")}
                    placeholder={t<string>("common.entity.dispatch.form.field.quantity.placeholder", "Quantity")}
                    precision={5}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    {...form.getInputProps(`splits.${index}.quantity`)} />
                  <UnitOfMeasureSelect
                    disabled={true}
                    label={t("common.entity.dispatch.form.field.uom", "Units")}
                    placeholder={t<string>("common.entity.dispatch.form.field.uom.placeholder", "Units")}
                    clearable
                    {...form.getInputProps(`splits.${index}.quantityUom`)} />
                </Group>
                <FreightTypeSelect
                  label={t("common.entity.dispatch.form.field.freighttype", "Freight Type")}
                  placeholder={t<string>("common.entity.dispatch.form.field.freighttype.placeholder", "Freight Type")}
                  selector={{
                    regionId: options?.regionId,
                    cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined
                  }}
                  clearable
                  {...form.getInputProps(`splits.${index}.freightType`)} />
                {category === transportCategory.water &&
                  <ServiceTypeSelect
                    label={t("common.entity.dispatch.form.field.servicetype", "Service Type")}
                    placeholder={t<string>("common.entity.dispatch.form.field.servicetype.placeholder", "Service Type")}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    clearable
                    {...form.getInputProps(`splits.${index}.serviceType`)} />
                }
              </Grid.Col>
              <Grid.Col span={8}>
                <Textarea
                  label={t("common.entity.dispatch.form.field.comments", "Comments")}
                  placeholder={t<string>("common.entity.dispatch.form.field.comments.placeholder", "Comment")}
                  {...form.getInputProps(`splits.${index}.comments`)} />
              </Grid.Col>
            </Grid>
          </Box>
        </>
      ))}
      <SplitActions
        onAddSplitActionClick={onAddSplitActionClick}
        pickupDisabled={pickupSplitDisabled}
        deliveryDisabled={deliverySplitDisabled}/>
      <Divider my="sm" variant="dotted"/>
      <TextInput
        label={t("common.entity.dispatch.form.field.workordernumber", "Work Order #")}
        placeholder={t<string>("common.entity.dispatch.form.field.workordernumber.placeholder", "Work Order #")}
        {...form.getInputProps("workOrderNumber")}
      />
      <Textarea
        label={t("common.entity.dispatch.form.field.comments", "Comments")}
        placeholder={t<string>("common.entity.dispatch.form.field.comments.placeholder", "Comment")}
        {...form.getInputProps("comments")} />
      {options?.copyDispatchRequestCommentsVisible === true &&
        <Switch
          label={t("common.entity.dispatch.form.field.copydispatchrequestcomments", "Include Request Comments")}
          placeholder={t<string>("common.entity.dispatch.form.field.copydispatchrequestcomments", "Include Request Comments")}
          {...form.getInputProps("copyDispatchRequestComments", {type: 'checkbox'})} />
      }
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface DispatchFormModalProps extends DispatchFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const DispatchFormModal = (props: DispatchFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <DispatchForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
